/**
 * FEATURE C2 · 2026-05-21 — useOfflineMutation
 *
 * Wrapper sobre react-query `useMutation` que detecta falha de rede
 * (`navigator.onLine === false` OU `ApiError(status=0)` OU genérico
 * `TypeError: Failed to fetch`) e enfileira a ação no outbox em vez de
 * propagar erro pra UI. O caller passa um `toOutboxItem` que mapeia as
 * variáveis em payload IDB.
 *
 * Quando enfileirado, o hook chama `onSuccess` normalmente com um
 * resultado sintético `{ queued: true, outboxId }` pra UI mostrar
 * "salvo offline" e fechar form/sheet do mesmo jeito que faria online.
 *
 * Quando online voltar (navigator online event) ou o SW receber sync,
 * `flushOutbox` é chamado automaticamente pelo `useOutboxAutoflush`
 * hook montado uma vez em App.tsx (segue abaixo neste arquivo).
 *
 * Type tradeoff: o sucesso real é `T` (response do server), mas no
 * caminho offline devolvemos sentinel. Convenção: caller verifica
 * `result.queued` para distinguir.
 */
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ApiError } from '@/api/client';
import { useAuth, isSessionValid } from '@/store/auth.store';
import {
  enqueueAction,
  flushOutbox,
  requestBackgroundSync,
  type OutboxItem,
} from '@/lib/offline-outbox';

export type OfflineMutationResult<T> =
  | { queued: false; data: T }
  | { queued: true; outboxId: string };

interface UseOfflineMutationOpts<TVariables, TData> {
  /** Função online — recebe variables, retorna response do server. */
  mutationFn: (variables: TVariables) => Promise<TData>;
  /**
   * Mapeia variables → outbox item (sem id/createdAt/attempts, que o
   * outbox preenche). Recebe variables e retorna o payload IDB.
   *
   * Quando undefined, falhas de rede NÃO são enfileiradas — comporta
   * como useMutation padrão. Isso permite escolher per-feature quais
   * mutations são offline-tolerantes.
   */
  toOutboxItem?: (variables: TVariables) =>
    | Omit<OutboxItem, 'id' | 'createdAt' | 'attempts'>
    | null;
  onSuccess?: (result: OfflineMutationResult<TData>, variables: TVariables) => void;
  onError?: (err: Error, variables: TVariables) => void;
  /** Pass-through pra options não cobertos acima. */
  mutationKey?: UseMutationOptions['mutationKey'];
}

function isNetworkError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (err instanceof ApiError) {
    // status 0 = our wrapper signal for fetch network failure.
    if (err.status === 0) return true;
    if (err.code === 'network_error') return true;
  }
  if (err instanceof TypeError && /fetch|network/i.test(err.message)) return true;
  return false;
}

export function useOfflineMutation<TVariables, TData>(
  opts: UseOfflineMutationOpts<TVariables, TData>,
) {
  return useMutation<OfflineMutationResult<TData>, Error, TVariables>({
    mutationKey: opts.mutationKey,
    mutationFn: async (variables: TVariables) => {
      try {
        const data = await opts.mutationFn(variables);
        return { queued: false, data };
      } catch (err) {
        if (opts.toOutboxItem && isNetworkError(err)) {
          const payload = opts.toOutboxItem(variables);
          if (payload) {
            const enq = await enqueueAction(payload);
            // Pede ao SW pra sincronizar quando voltar (best-effort).
            void requestBackgroundSync();
            return { queued: true, outboxId: enq.id };
          }
        }
        throw err;
      }
    },
    onSuccess: (result, variables) => opts.onSuccess?.(result, variables),
    onError: (err, variables) => opts.onError?.(err, variables),
  });
}

/**
 * Mount-once hook que escuta `online` + `visibilitychange` e dispara
 * flush do outbox. Usado pelo App ou pelo OfflineIndicator.
 *
 * Idempotente: não há gate global, mas `flushOutbox` é serial e
 * tolerante a chamadas paralelas (cada item só é deletado ao confirmar
 * sucesso).
 */
export function useOutboxAutoflush(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // P3 fix · 2026-05-22 — gate por sessão válida.
    // O autoflush é montado em AppRoot (fora do RequireAuth) pra
    // sobreviver troca de rota. Mas chamar `flushOutbox()` quando o
    // usuário está numa página pública (sem token) faz cada item do
    // outbox bater 401 e suja o console. `flushOutbox` agora também
    // checa internamente, mas evitamos o round-trip do IDB aqui.
    const hasSession = () => {
      try {
        return isSessionValid(useAuth.getState());
      } catch {
        return false;
      }
    };
    const handler = () => {
      if (navigator.onLine === false) return;
      if (!hasSession()) return;
      void flushOutbox();
    };
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible' && navigator.onLine && hasSession()) {
        void flushOutbox();
      }
    };
    window.addEventListener('online', handler);
    document.addEventListener('visibilitychange', visibilityHandler);
    // Tenta flush no boot caso já esteja online (cobre o caso "fechei
    // o tab offline, abri agora online").
    if (navigator.onLine && hasSession()) {
      void flushOutbox();
    }
    return () => {
      window.removeEventListener('online', handler);
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
  }, []);
}
